import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes, tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

interface ImmoForm {
  asset_id: string;
  acquisition_date: string;
  acquisition_value_tnd: string;
  duration_years: number;
  method: 'linear' | 'degressive' | 'exceptional';
}

interface Step {
  step_number: number;
  step_name: string;
  status: 'done' | 'in_progress' | 'blocked' | 'pending' | 'skipped';
  details: string;
  blockers: string[];
}

interface ClotureurResult {
  fiscal_year: number;
  company_name: string;
  steps: Step[];
  overall_status: 'ready' | 'partial' | 'blocked';
  summary: {
    total_amortissement_millimes: number;
    taxable_income_after_amort_millimes: number;
    is_due_millimes: number;
    acomptes_n_plus_1: Array<{ due_date: string; amount_millimes: number }>;
  };
}

const STATUS_STYLE: Record<Step['status'], { label: string; cls: string; icon: string }> = {
  done: { label: 'Terminée', cls: 'bg-emerald-100 text-emerald-900 border-emerald-300', icon: '✓' },
  in_progress: { label: 'En cours', cls: 'bg-blue-100 text-blue-900 border-blue-300', icon: '◐' },
  blocked: { label: 'Bloquée', cls: 'bg-red-100 text-red-900 border-red-300', icon: '✗' },
  pending: { label: 'À faire', cls: 'bg-zinc-100 text-zinc-700 border-zinc-300', icon: '○' },
  skipped: { label: 'Ignorée', cls: 'bg-zinc-50 text-zinc-500 border-zinc-200', icon: '−' },
};

const OVERALL_STYLE = {
  ready: { label: 'Prête', cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  partial: { label: 'Partielle', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  blocked: { label: 'Bloquée', cls: 'bg-red-100 text-red-900 border-red-300' },
};

const SAMPLE_IMMO: ImmoForm[] = [
  { asset_id: 'IMMO-1', acquisition_date: '2024-01-01', acquisition_value_tnd: '10000.000', duration_years: 5, method: 'linear' },
  { asset_id: 'IMMO-2', acquisition_date: '2025-06-15', acquisition_value_tnd: '25000.000', duration_years: 10, method: 'degressive' },
];

export function ClotureurPage() {
  const [fiscalYear, setFiscalYear] = useState(2026);
  const [start, setStart] = useState('2026-01-01');
  const [end, setEnd] = useState('2026-12-31');
  const [companyName, setCompanyName] = useState('Sahel Textile Industries');
  const [sector, setSector] = useState('');
  const [immos, setImmos] = useState<ImmoForm[]>(SAMPLE_IMMO);
  const [taxableIncome, setTaxableIncome] = useState('50000.000');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  function update(i: number, patch: Partial<ImmoForm>) {
    setImmos((p) => p.map((im, idx) => (idx === i ? { ...im, ...patch } : im)));
  }
  function addImmo() {
    setImmos((p) => [...p, { asset_id: `IMMO-${p.length + 1}`, acquisition_date: '2026-01-01', acquisition_value_tnd: '0.000', duration_years: 5, method: 'linear' }]);
  }
  function removeImmo(i: number) {
    setImmos((p) => p.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload: Record<string, unknown> = {
        fiscal_year: fiscalYear,
        fiscal_year_start: start,
        fiscal_year_end: end,
        company_name: companyName,
        immobilisations: immos.map((im) => ({
          asset_id: im.asset_id,
          acquisition_date: im.acquisition_date,
          acquisition_value_millimes: tndStringToMillimes(im.acquisition_value_tnd),
          duration_years: im.duration_years,
          method: im.method,
        })),
        taxable_income_before_amort_millimes: tndStringToMillimes(taxableIncome),
      };
      if (sector) payload.sector_code = sector;
      const res = await api.invokeAgent('clotureur', { payload, fiscal_reference_date: end });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Clôturier"
      module="Comptabilité · Phase 2"
      model="Opus 4.7"
      description={
        <>
          Pilote la clôture annuelle 12 étapes — collecte, classification, rapprochement,
          amortissements, cut-off, provisions, IS, journal, états financiers, liasse, contrôles,
          PDFs. L'agent appelle engine-amortissement et engine-is, agrège les findings et expose
          la checklist.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Exercice à clôturer">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Société"><input className={inputCls} value={companyName} onChange={(e) => setCompanyName(e.target.value)} required /></Field>
            <Field label="Année fiscale"><input type="number" className={inputCls} value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value || '2026', 10))} /></Field>
            <Field label="Secteur (optionnel)">
              <select className={inputCls} value={sector} onChange={(e) => setSector(e.target.value)}>
                <option value="">Standard (15%)</option>
                <option value="industrie_export">Industrie export (10%)</option>
                <option value="banque_assurance">Banque/Assurance (25%)</option>
                <option value="hydrocarbures">Hydrocarbures (35%)</option>
              </select>
            </Field>
            <Field label="Début exercice"><input type="date" className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} required /></Field>
            <Field label="Fin exercice"><input type="date" className={inputCls} value={end} onChange={(e) => setEnd(e.target.value)} required /></Field>
            <Field label="Bénéfice avant amort. (TND)"><input className={inputCls + ' font-mono'} value={taxableIncome} onChange={(e) => setTaxableIncome(e.target.value)} required /></Field>
          </div>
        </FormCard>

        <FormCard
          title={`Immobilisations (${immos.length})`}
          action={<button type="button" onClick={addImmo} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90">+ Ajouter</button>}
        >
          <div className="space-y-2">
            {immos.map((im, idx) => (
              <div key={idx} className="border border-border rounded p-3 bg-cream/30 grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                <Field label="ID actif"><input className={inputCls + ' font-mono'} value={im.asset_id} onChange={(e) => update(idx, { asset_id: e.target.value })} required /></Field>
                <Field label="Date acquisition"><input type="date" className={inputCls} value={im.acquisition_date} onChange={(e) => update(idx, { acquisition_date: e.target.value })} required /></Field>
                <Field label="Valeur (TND)"><input className={inputCls + ' font-mono'} value={im.acquisition_value_tnd} onChange={(e) => update(idx, { acquisition_value_tnd: e.target.value })} required /></Field>
                <Field label="Durée (ans)"><input type="number" className={inputCls} value={im.duration_years} onChange={(e) => update(idx, { duration_years: parseInt(e.target.value || '5', 10) })} min={1} max={50} /></Field>
                <Field label="Méthode">
                  <select className={inputCls} value={im.method} onChange={(e) => update(idx, { method: e.target.value as ImmoForm['method'] })}>
                    <option value="linear">Linéaire</option>
                    <option value="degressive">Dégressif</option>
                    <option value="exceptional">Exceptionnel</option>
                  </select>
                </Field>
                {immos.length > 1 && <button type="button" onClick={() => removeImmo(idx)} className="text-xs text-red-700 hover:text-red-900 self-end">Supprimer</button>}
              </div>
            ))}
          </div>
        </FormCard>

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Calcul de clôture…' : 'Lancer la clôture'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <ClotureurResultView result={result as ClotureurResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function ClotureurResultView({ result }: { result: ClotureurResult }) {
  const ov = OVERALL_STYLE[result.overall_status];
  return (
    <div className="space-y-5">
      <section className={`p-3 rounded border-2 ${ov.cls} flex items-center gap-3 flex-wrap`}>
        <span className="text-xs uppercase tracking-wider">Statut clôture</span>
        <strong className="font-serif text-lg">{ov.label}</strong>
        <span className="text-xs">Exercice {result.fiscal_year}</span>
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Synthèse fiscale</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
          <Stat label="Dotation amortissement" value={formatMillimes(result.summary.total_amortissement_millimes)} />
          <Stat label="Bénéfice après amort." value={formatMillimes(result.summary.taxable_income_after_amort_millimes)} />
          <Stat label="IS dû" value={formatMillimes(result.summary.is_due_millimes)} highlight />
        </div>
        {result.summary.acomptes_n_plus_1.length > 0 && (
          <div className="mt-3">
            <div className="text-xs uppercase tracking-wider text-taupe mb-1">Acomptes N+1</div>
            <ul className="text-sm space-y-1">
              {result.summary.acomptes_n_plus_1.map((a, i) => (
                <li key={i} className="p-2 rounded border border-border bg-white flex items-center justify-between">
                  <span className="font-mono">{a.due_date}</span>
                  <span className="font-mono">{formatMillimes(a.amount_millimes)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Checklist 12 étapes</div>
        <ol className="space-y-2">
          {result.steps.map((s) => {
            const sty = STATUS_STYLE[s.status];
            return (
              <li key={s.step_number} className={`p-3 rounded border ${sty.cls}`}>
                <div className="flex items-start gap-3">
                  <div className="text-2xl leading-none">{sty.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs uppercase tracking-wider">Étape {s.step_number}</span>
                      <strong>{s.step_name}</strong>
                      <span className="text-xs">{sty.label}</span>
                    </div>
                    <div className="text-sm mt-1">{s.details}</div>
                    {s.blockers.length > 0 && (
                      <ul className="mt-2 text-xs space-y-1">
                        {s.blockers.map((b, i) => (
                          <li key={i} className="text-red-800">⚠ {b}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`p-3 rounded border text-center ${highlight ? 'border-gold bg-gold/10' : 'border-border bg-white'}`}>
      <div className="text-xs uppercase tracking-wider text-taupe">{label}</div>
      <div className={`font-mono text-lg ${highlight ? 'font-bold' : ''}`}>{value}</div>
    </div>
  );
}
